import { z } from 'zod';
import { idSchema } from './ids';
import { type Cents, fromCents, toCents } from './money';
import { yearMonthSchema } from './period';

/**
 * Turnover (FR-TURN-001…010): per month, the Fact sheet's "Invoice Planned" (Plāns) and
 * "Invoiced" (Fakts) of every project (decision Q14), comments and "under question" flags per
 * project × month, and the invoices planned after the month (Outstanding).
 */
export const TURNOVER_SOURCES = ['both', 'plan', 'fact'] as const;
export type TurnoverSource = (typeof TURNOVER_SOURCES)[number];
export type TurnoverRowSource = Exclude<TurnoverSource, 'both'>;

export const turnoverQuerySchema = z.object({
  year: z.coerce.number().int().min(2000).max(2100),
  month: z.coerce.number().int().min(1).max(12),
  source: z.enum(TURNOVER_SOURCES).default('both'),
});
export type TurnoverQuery = z.input<typeof turnoverQuerySchema>;

export const turnoverRowSchema = z.object({
  projectId: idSchema,
  projectName: z.string(),
  completion: z.number().nullable(),
  source: z.enum(['plan', 'fact']),
  amount: z.string(),
});
export type TurnoverRow = z.infer<typeof turnoverRowSchema>;

export const turnoverAnnotationSchema = z.object({
  projectId: idSchema,
  comment: z.string(),
  flagged: z.boolean(),
});
export type TurnoverAnnotation = z.infer<typeof turnoverAnnotationSchema>;

export const outstandingRowSchema = z.object({
  projectId: idSchema,
  projectName: z.string(),
  amount: z.string(),
  months: z.array(yearMonthSchema.extend({ amount: z.string() })),
  /** An open project with no invoice planned after the month (FR-TURN-010). */
  warning: z.boolean(),
});
export type OutstandingRow = z.infer<typeof outstandingRowSchema>;

export const turnoverSchema = z.object({
  /** Amount > 0 only, largest first (FR-TURN-003). */
  rows: z.array(turnoverRowSchema),
  /** Comments and flags of this month for the projects in `rows`. */
  annotations: z.array(turnoverAnnotationSchema),
  outstanding: z.object({ rows: z.array(outstandingRowSchema), total: z.string() }),
});
export type Turnover = z.infer<typeof turnoverSchema>;

export const turnoverAnnotationInputSchema = yearMonthSchema.extend({
  comment: z.string().trim().max(2000).optional(),
  flagged: z.boolean().optional(),
});

/**
 * The summary cards (FR-TURN-004/007): total, projects with turnover — each project once
 * (decision Q14) — and the amount and projects under question. Computed on both sides so a
 * flag click updates the card at once.
 */
export function summarizeTurnover(rows: readonly TurnoverRow[], flagged: ReadonlySet<string>) {
  const total: Cents = rows.reduce((s, r) => s + toCents(r.amount), 0);
  const flaggedRows = rows.filter((r) => flagged.has(r.projectId));
  return {
    total: fromCents(total),
    projects: new Set(rows.map((r) => r.projectId)).size,
    flaggedAmount: fromCents(flaggedRows.reduce((s, r) => s + toCents(r.amount), 0)),
    flaggedProjects: new Set(flaggedRows.map((r) => r.projectId)).size,
  };
}
